import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Paper, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

const TOUR_KEY = 'dashboard-tour-dismissed';

interface Step {
  selector: string;
  /** i18n key for title under common:dashboardTour.steps.<key>.title */
  key: string;
  placement: 'bottom' | 'left' | 'top' | 'right';
}

const steps: Step[] = [
  { selector: '[data-tour="add-widgets"]', key: 'addWidgets', placement: 'left' },
  { selector: '[data-tour="drag-handle"]', key: 'moveWidgets', placement: 'right' },
  { selector: '[data-tour-widget="first"] .react-resizable-handle', key: 'resizeWidgets', placement: 'top' },
  { selector: '[data-tour="widget-menu"]', key: 'widgetOptions', placement: 'left' },
];

const DashboardTour: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  // The same edge the Setup Guide cards carry: primary blue at 24% alpha. Resolved to a colour
  // string rather than a theme callback because the arrow below is positioned with a plain
  // React.CSSProperties object, not `sx`, and both have to be the identical colour or the seam
  // between card and arrow shows.
  const guideEdge = alpha(theme.palette.primary.main, 0.24);
  const [visible, setVisible] = useState(
    () => localStorage.getItem(TOUR_KEY) !== 'true'
  );
  const [current, setCurrent] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const rafRef = useRef(0);

  const dismiss = useCallback(() => {
    localStorage.setItem(TOUR_KEY, 'true');
    setVisible(false);
    setCurrent(0);
    setRect(null);
  }, []);

  // Listen for show-setup-guide event to re-trigger tour
  useEffect(() => {
    const handler = () => {
      setRect(null);
      setCurrent(0);
      setVisible(true);
    };
    window.addEventListener('show-setup-guide', handler);
    return () => window.removeEventListener('show-setup-guide', handler);
  }, []);

  // Clear rect immediately on step change to avoid stale position flash
  useEffect(() => {
    setRect(null);
  }, [current]);

  // Track target element position
  const measure = useCallback(() => {
    if (!visible) return;
    const el = document.querySelector(steps[current].selector);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect(prev =>
        prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
          ? prev
          : r
      );
    } else {
      rafRef.current = requestAnimationFrame(measure);
    }
  }, [visible, current]);

  useEffect(() => {
    if (visible) {
      const delay = rect === null ? 50 : 300;
      const timer = setTimeout(() => {
        measure();
      }, delay);
      return () => {
        clearTimeout(timer);
        cancelAnimationFrame(rafRef.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, measure, current]);

  if (!visible || !rect) return null;

  const pad = 6;
  const step = steps[current];
  const isLast = current === steps.length - 1;

  // Compute tooltip position
  const tooltipStyle: React.CSSProperties = { position: 'fixed', zIndex: 1600 };
  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    width: 10,
    height: 10,
    background: '#fff',
    transform: 'rotate(45deg)',
    zIndex: -1,
  };

  if (step.placement === 'bottom') {
    tooltipStyle.top = rect.bottom + pad + 10;
    tooltipStyle.left = Math.max(16, Math.min(rect.left + rect.width / 2, window.innerWidth - 160));
    tooltipStyle.transform = 'translateX(-50%)';
    arrowStyle.top = -5;
    arrowStyle.left = '50%';
    arrowStyle.marginLeft = -5;
    // Arrow points UP. After rotate(45deg) the two edges facing away from the card are the
    // square's original top and left, so only those get the border — bordering all four would
    // draw a line across the arrow's waist where the card's own border crosses it.
    arrowStyle.borderTop = `1px solid ${guideEdge}`;
    arrowStyle.borderLeft = `1px solid ${guideEdge}`;
  } else if (step.placement === 'top') {
    tooltipStyle.bottom = window.innerHeight - rect.top + pad + 10;
    tooltipStyle.left = Math.max(16, Math.min(rect.left + rect.width / 2, window.innerWidth - 160));
    tooltipStyle.transform = 'translateX(-50%)';
    arrowStyle.bottom = -5;
    arrowStyle.left = '50%';
    arrowStyle.marginLeft = -5;
    arrowStyle.borderBottom = `1px solid ${guideEdge}`;   // arrow points DOWN
    arrowStyle.borderRight = `1px solid ${guideEdge}`;
  } else if (step.placement === 'left') {
    tooltipStyle.top = rect.top + rect.height / 2;
    tooltipStyle.right = window.innerWidth - rect.left + pad + 10;
    tooltipStyle.transform = 'translateY(-50%)';
    arrowStyle.right = -5;
    arrowStyle.top = '50%';
    arrowStyle.marginTop = -5;
    arrowStyle.borderTop = `1px solid ${guideEdge}`;      // arrow points RIGHT
    arrowStyle.borderRight = `1px solid ${guideEdge}`;
  } else {
    tooltipStyle.top = rect.top + rect.height / 2;
    tooltipStyle.left = rect.right + pad + 10;
    tooltipStyle.transform = 'translateY(-50%)';
    arrowStyle.left = -5;
    arrowStyle.top = '50%';
    arrowStyle.marginTop = -5;
    arrowStyle.borderBottom = `1px solid ${guideEdge}`;   // arrow points LEFT
    arrowStyle.borderLeft = `1px solid ${guideEdge}`;
  }

  // Spotlight clip-path: full overlay with a rectangular cutout
  const clip = `polygon(
    0% 0%, 0% 100%, 
    ${rect.left - pad}px 100%, 
    ${rect.left - pad}px ${rect.top - pad}px, 
    ${rect.right + pad}px ${rect.top - pad}px, 
    ${rect.right + pad}px ${rect.bottom + pad}px, 
    ${rect.left - pad}px ${rect.bottom + pad}px, 
    ${rect.left - pad}px 100%, 
    100% 100%, 100% 0%
  )`;

  return (
    <>
      {/* Click catcher — behind tooltip but above content */}
      <Box
        onClick={dismiss}
        sx={{ position: 'fixed', inset: 0, zIndex: 1500 }}
      />

      {/* Visual overlay */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.45)',
          zIndex: 1501,
          clipPath: clip,
          pointerEvents: 'none',
        }}
      />

      {/* Spotlight border ring */}
      <Box
        sx={{
          position: 'fixed',
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          borderRadius: '6px',
          border: '2px solid',
          borderColor: 'primary.main',
          zIndex: 1502,
          pointerEvents: 'none',
          boxShadow: '0 0 0 4px rgba(46,101,212,0.18)',
        }}
      />

      {/* Tooltip. The 1px primary-blue-at-24% edge is the Setup Guide's, so the dashboard's
          guidance reads as the same family as every other module's onboarding card. It was the
          only guidance surface still relying on elevation alone. The shadow stays — unlike an
          in-page card (svdy-ui §1), a floating popover over a dimmed overlay is legitimately
          elevated; the border is what makes it recognisable, not what replaces the lift. */}
      <Paper elevation={8} sx={{
        ...tooltipStyle, p: 2, borderRadius: 2, maxWidth: 280, minWidth: 220,
        border: '1px solid', borderColor: guideEdge,
      }}>
        <Box sx={{ ...arrowStyle, boxShadow: '-1px -1px 2px rgba(0,0,0,0.06)' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>
          {t(`common:dashboardTour.steps.${step.key}.title`)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1.5, lineHeight: 1.5 }}>
          {t(`common:dashboardTour.steps.${step.key}.description`)}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.disabled">
            {current + 1} / {steps.length}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={dismiss}
              sx={{ textTransform: 'none', fontSize: '0.78rem', color: 'text.secondary', minWidth: 0, px: 1 }}>
              {t('common:dashboardTour.skip')}
            </Button>
            <Button size="small" variant="contained"
              onClick={() => isLast ? dismiss() : setCurrent(c => c + 1)}
              sx={{ textTransform: 'none', fontSize: '0.78rem', borderRadius: 1, minWidth: 0, px: 1.5 }}>
              {isLast ? t('common:dashboardTour.done') : t('common:dashboardTour.next')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default DashboardTour;
